"use client";

import { useState } from "react";
import {
  type BarCategory,
  BarChart,
} from "@/components/tool-kit/charts/bar-chart";
import {
  ChartStyleFields,
  sortOptions,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type BarMode,
  type BarOrientation,
  rowTotal,
  type SortOrder,
  sortIndices,
} from "@/lib/tool-kit/charts/bars";
import { MAX_SERIES } from "@/lib/tool-kit/charts/color";
import { type NumberFormat, valueText } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type LabeledTable,
  parseLabeledRows,
  seriesNames,
} from "@/lib/tool-kit/charts/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const BAR_GRAPH_MAKER_STRINGS = {
  dataLabel: "Your data — one category per line: label, value",
  dataHint:
    "Add more values on a line for more series. Separate cells with commas, tabs or semicolons — use semicolons if your numbers have decimal commas. A first line without numbers names the columns.",
  exampleData:
    "Study location, Students\nHome, 51\nLibrary, 34\nStudy room, 28\nCoffee shop, 17",
  firstLineHeader: "The first line names the columns",
  exampleTitle: "Preferred Study Location",
  exampleCategoryTitle: "Study location",
  exampleValueTitle: "Number of students",
  categoriesDetected: "one:{count} category|other:{count} categories",
  seriesDetected: "one:{count} series|other:{count} series",
  seriesDefault: "Series {number}",
  orientationLabel: "Bars",
  vertical: "Vertical (columns)",
  horizontal: "Horizontal",
  modeLabel: "Several series",
  grouped: "Grouped side by side",
  stacked: "Stacked",
  sortLabel: "Order of the bars",
  sortNone: "As entered",
  sortAscending: "Smallest first",
  sortDescending: "Largest first",
  labelsAndStyle: "Labels and style",
  chartTitle: "Chart title",
  xAxisTitle: "Category axis title",
  yAxisTitle: "Value axis title",
  axisTitlePlaceholderX: "Category",
  axisTitlePlaceholderY: "Value",
  colorLabel: "Bar color (the first series)",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabels: "Show the values on the bars",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and the gridlines out of the plot, and sets the figure number in bold and the title in italics above the chart — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the data as a table",
  categoryColumn: "Category",
  chartLabel: "Bar graph",
  chartDescription:
    "Bar graph of {categories} and {series}. Largest value: {maxLabel}, {max}. Smallest value: {minLabel}, {min}.",
  enterData:
    "Enter at least one line with a label and a number to draw the graph.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  invalidCells:
    "one:{count} cell is not a number and is drawn as missing.|other:{count} cells are not numbers and are drawn as missing.",
  tooManySeries:
    "Only the first {max} series are drawn — beyond that, colors can no longer be told apart.",
} as const;

type Strings = typeof BAR_GRAPH_MAKER_STRINGS;

const MISSING = "\u2014";

/** The `<desc>` a screen reader announces: counts, and the largest and smallest bars. */
function summarize(
  categories: BarCategory[],
  names: string[],
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string {
  let max: { label: string; value: number } | null = null;
  let min: { label: string; value: number } | null = null;
  for (const category of categories) {
    for (const value of category.values) {
      if (value !== null && (!max || value > max.value)) {
        max = { label: category.label, value };
      }
      if (value !== null && (!min || value < min.value)) {
        min = { label: category.label, value };
      }
    }
  }
  return t(strings.chartDescription, {
    categories: tPlural(strings.categoriesDetected, locale, categories.length, {
      count: fmt(categories.length),
    }),
    series: tPlural(strings.seriesDetected, locale, names.length, {
      count: fmt(names.length),
    }),
    maxLabel: max?.label ?? "",
    max: max ? valueText(fmt, max.value) : "",
    minLabel: min?.label ?? "",
    min: min ? valueText(fmt, min.value) : "",
  });
}

/** What the reader should know about how their data was read. */
function barNotices(
  table: LabeledTable,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (table.rows.length === 0) {
    notes.push(strings.enterData);
  }
  if (table.skipped.length > 0) {
    notes.push(
      tPlural(strings.skippedLines, locale, table.skipped.length, {
        lines: table.skipped.map((n) => fmt(n)).join(", "),
      })
    );
  }
  if (table.invalidCells > 0) {
    notes.push(
      tPlural(strings.invalidCells, locale, table.invalidCells, {
        count: fmt(table.invalidCells),
      })
    );
  }
  if (table.seriesCount > MAX_SERIES) {
    notes.push(t(strings.tooManySeries, { max: fmt(MAX_SERIES) }));
  }
  return notes;
}

export default function BarGraphMakerWidget() {
  const strings = useToolUi(BAR_GRAPH_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [orientation, setOrientation] = useState<BarOrientation>("vertical");
  const [mode, setMode] = useState<BarMode>("grouped");
  const [sort, setSort] = useState<SortOrder>("none");
  const [headerChoice, setHeaderChoice] = useState<boolean | null>(null);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleCategoryTitle,
    yTitle: strings.exampleValueTitle,
    color: "blue",
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const table = parseLabeledRows(raw, { header: headerChoice ?? undefined });
  const seriesCount = Math.min(table.seriesCount, MAX_SERIES);
  const names = seriesNames(table, (index) =>
    t(strings.seriesDefault, { number: fmt(index + 1) })
  ).slice(0, seriesCount);
  const rows: BarCategory[] = table.rows.map((row) => ({
    label: row.label,
    values: row.values.slice(0, seriesCount),
  }));
  const keys = rows.map((row) =>
    seriesCount === 1 ? (row.values[0] ?? Number.NaN) : rowTotal(row.values)
  );
  const categories = sortIndices(keys, sort).map((index) => rows[index]);
  const head = [table.header?.[0]?.trim() || strings.categoryColumn, ...names];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="bgm-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={7}
        value={raw}
      />
      <Checkbox
        checked={headerChoice ?? table.autoHeader}
        label={strings.firstLineHeader}
        onChange={setHeaderChoice}
      />
      <div aria-live="polite" className="space-y-2">
        {categories.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.categoriesDetected, locale, categories.length, {
              count: fmt(categories.length),
            })}
            {" · "}
            {tPlural(strings.seriesDetected, locale, seriesCount, {
              count: fmt(seriesCount),
            })}
          </p>
        ) : null}
        {barNotices(table, strings, locale, fmt).map((notice) => (
          <Notice key={notice}>{notice}</Notice>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.orientationLabel}
          name="bgm-orientation"
          onChange={setOrientation}
          options={[
            { value: "vertical", label: strings.vertical },
            { value: "horizontal", label: strings.horizontal },
          ]}
          value={orientation}
        />
        {seriesCount > 1 ? (
          <PillGroup
            label={strings.modeLabel}
            name="bgm-mode"
            onChange={setMode}
            options={[
              { value: "grouped", label: strings.grouped },
              { value: "stacked", label: strings.stacked },
            ]}
            value={mode}
          />
        ) : null}
      </div>
      {categories.length > 0 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...categories.map((c) => [c.label, ...c.values])])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "bar-graph")}
          labels={strings}
          note={style.note}
          table={{
            head,
            rows: categories.map((c) => [
              c.label,
              ...c.values.map((v) =>
                v === null ? MISSING : valueText(fmt, v)
              ),
            ]),
          }}
          title={style.title}
        >
          <BarChart
            categories={categories}
            description={summarize(categories, names, strings, locale, fmt)}
            fmt={fmt}
            id="bgm-chart"
            label={style.title.trim() || strings.chartLabel}
            mode={mode}
            names={names}
            orientation={orientation}
            style={style}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        axisLabels={strings}
        colorLabels={strings}
        idPrefix="bgm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabels}
      >
        <SelectField
          id="bgm-sort"
          label={strings.sortLabel}
          onChange={setSort}
          options={sortOptions(strings)}
          value={sort}
        />
      </ChartStyleFields>
    </div>
  );
}
